import React from "react";
import {
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell, ScatterChart, Scatter, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
  AreaChart, Area,
  ComposedChart,
  RadialBarChart, RadialBar,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Treemap
} from "recharts";
import { tooltipContentStyle, tooltipItemStyle, tooltipLabelStyle } from "./ChartStyles";
import { formatNumber } from "../../Utils/formaters";

const DynamicChart = ({ spec, data, showLegend = true, showAxis = true, showGrid = true }) => {
  if (!spec || !data || !data.length) return null;

  const {
    type,
    xKey,
    yKeys,
    colorScheme = ["#F3722C", "#3A0CA3", "#4CC9F0"],
    stackId = "stack",
    seriesTypes = [],
  } = spec;

  return (
    <>
      {(type === "bar" || type === "bar-simple" || type === "bar-multi") && (
          <BarChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip
              contentStyle={tooltipContentStyle}
              itemStyle={tooltipItemStyle}
              labelStyle={tooltipLabelStyle}
              formatter={(value) => formatNumber(value)}
            />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <Bar key={key} dataKey={key} fill={colorScheme[i % colorScheme.length]} />
            ))}
          </BarChart>
      )}

      {(type === "line" || type === "line-multi" || type === "multi-line") && (
        
          <LineChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <Line key={key} type="monotone" dataKey={key} stroke={colorScheme[i % colorScheme.length]} strokeWidth={2} />
            ))}
          </LineChart>
        
      )}

      {type === "line-simple" && (
        
          <LineChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend />}
            <Line type="monotone" dataKey={yKeys[0]} stroke={colorScheme[0]} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        
      )}

      {type === "area" && (
        
          <AreaChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip
              contentStyle={tooltipContentStyle}
              itemStyle={tooltipItemStyle}
              labelStyle={tooltipLabelStyle}
              formatter={(value) => formatNumber(value)}
            />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={colorScheme[i % colorScheme.length]}
                fill={colorScheme[i % colorScheme.length]}
                fillOpacity={0.25}
              />
            ))}
          </AreaChart>
        
      )}

      {type === "area-stacked" && (
        
          <AreaChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip
              contentStyle={tooltipContentStyle}
              itemStyle={tooltipItemStyle}
              labelStyle={tooltipLabelStyle}
              formatter={(value) => formatNumber(value)}
            />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stackId={stackId}
                stroke={colorScheme[i % colorScheme.length]}
                fill={colorScheme[i % colorScheme.length]}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        
      )}

      {type === "pie" && (
        
          <PieChart responsive style={{ width: "100%", height: "100%" }}>
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend formatter={(value) => formatNumber(value)} />}
            <Pie data={data} dataKey={yKeys[0]} nameKey={xKey} cx="50%" cy="50%" outerRadius={100}>
              {data.map((_, i) => (
                <Cell key={i} fill={colorScheme[i % colorScheme.length]} />
              ))}
            </Pie>
          </PieChart>
        
      )}

      {type === "donut" && (
        
          <PieChart responsive style={{ width: "100%", height: "100%" }}>
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend />}
            <Pie
              data={data}
              dataKey={yKeys[0]}
              nameKey={xKey}
              cx="50%"
              cy="50%"
              innerRadius="45%"
              outerRadius="75%"
              paddingAngle={2}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={colorScheme[i % colorScheme.length]} />
              ))}
            </Pie>
          </PieChart>
        
      )}

      {type === "scatter" && (
        
          <ScatterChart responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} tickFormatter={formatNumber} />}
            {showAxis && <YAxis dataKey={yKeys[0]} tickFormatter={formatNumber} />}
            <Tooltip formatter={(value) => formatNumber(value)} />
            <Scatter data={data} fill={colorScheme[0]} />
          </ScatterChart>
        
      )}

      {type === "bar-stacked" && (
        
          <BarChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                stackId={stackId}
                fill={colorScheme[i % colorScheme.length]}
              />
            ))}
          </BarChart>
        
      )}

      {type === "composed" && (
        
          <ComposedChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {showAxis && <XAxis dataKey={xKey} />}
            {showAxis && <YAxis tickFormatter={formatNumber} />}
            <Tooltip
              contentStyle={tooltipContentStyle}
              itemStyle={tooltipItemStyle}
              labelStyle={tooltipLabelStyle}
              formatter={(value) => formatNumber(value)}
            />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => {
              const chartKind = (seriesTypes[i] || "line").toLowerCase();
              const color = colorScheme[i % colorScheme.length];

              if (chartKind === "bar") {
                return <Bar key={key} dataKey={key} fill={color} />;
              }

              if (chartKind === "area") {
                return (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={color}
                    fill={color}
                    fillOpacity={0.2}
                  />
                );
              }

              if (chartKind === "scatter") {
                return <Scatter key={key} name={key} data={data} fill={color} />;
              }

              return <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={false} />;
            })}
          </ComposedChart>
        
      )}

      {type === "radar" && (
        
          <RadarChart data={data} responsive style={{ width: "100%", height: "100%" }}>
            {showGrid && <PolarGrid />}
            <PolarAngleAxis dataKey={xKey} />
            <PolarRadiusAxis tickFormatter={formatNumber} />
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <Radar
                key={key}
                name={key}
                dataKey={key}
                stroke={colorScheme[i % colorScheme.length]}
                fill={colorScheme[i % colorScheme.length]}
                fillOpacity={0.35}
              />
            ))}
          </RadarChart>
        
      )}

      {type === "radial-bar" && (
        
          <RadialBarChart
            innerRadius="30%"
            outerRadius="90%"
            data={data}
            startAngle={90}
            endAngle={-270}
            responsive style={{ width: "100%", height: "100%" }}
          >
            <Tooltip formatter={(value) => formatNumber(value)} />
            {showLegend && <Legend />}
            {yKeys.map((key, i) => (
              <RadialBar
                key={key}
                minAngle={15}
                background
                clockWise
                dataKey={key}
                fill={colorScheme[i % colorScheme.length]}
              />
            ))}
          </RadialBarChart>
        
      )}

      {type === "treemap" && (
        
          <Treemap
            data={data}
            dataKey={yKeys[0]}
            nameKey={xKey}
            stroke="#fff"
            fill={colorScheme[0]}
            responsive style={{ width: "100%", height: "100%" }}
          >
            <Tooltip formatter={(value) => formatNumber(value)} />
          </Treemap>
        
      )}
    </>
  );
};

export default DynamicChart;
